import React, { useState, useMemo } from "react";
import { Card, Row, Col, Spinner, Alert, Form, InputGroup, Nav, Tab, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faInfoCircle,
    faChartBar,
    faSearch,
    faTimes,
    faShoppingCart,
    faReceipt,
    faExclamationTriangle,
    faCalendarAlt,
    faPlay,
    faFileImport,
    faPlus,
    faEdit,
    faTrash,
    faBriefcase,
    faClock,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { movimentacaoBreadcrumbItems, getMovimentoTipoBadge } from "data/sngpc";
import {
    useInventarioAtivo,
    useMovimentos,
    useEstatisticasMovimentos,
    useJanelaTemporalAtiva
} from "hooks/useSngpc";
import { useEmpresas } from "providers/EmpresaProvider";
import Button from "components/base/Button";
import { format } from 'date-fns/format';
import LinkProduto from "../../../components/common/LinkProduto";
import ModalCriarMovimento from "./components/ModalCriarMovimento";

const MovimentacaoSngpc: React.FC = () => {
    // Estados para modais
    const [showModalCriarMovimento, setShowModalCriarMovimento] = useState(false);
    const [tipoMovimentoCriar, setTipoMovimentoCriar] = useState<'ENTRADA' | 'VENDA_CONSUMIDOR' | 'PERDA'>('ENTRADA');

    const [showModalEditarMovimento, setShowModalEditarMovimento] = useState(false);
    const [movimentoParaEditar, setMovimentoParaEditar] = useState<any>(null);

    const [showModalExcluirMovimento, setShowModalExcluirMovimento] = useState(false);
    const [movimentoParaExcluir, setMovimentoParaExcluir] = useState<any>(null);

    const [showModalImportarLotes, setShowModalImportarLotes] = useState(false);
    const [tipoImportacao, setTipoImportacao] = useState<'vendas' | 'compras'>('vendas');

    // Estados para filtros
    const [termoBusca, setTermoBusca] = useState('');
    const [abaAtiva, setAbaAtiva] = useState<'todas' | 'ENTRADA' | 'VENDA_CONSUMIDOR' | 'PERDA'>('todas');

    // Hooks
    const { selectedIds, empresas } = useEmpresas();
    const inventarioQuery = useInventarioAtivo();
    const inventario = inventarioQuery.data?.data;

    // Filtros para movimentos
    const filtrosMovimentos = useMemo(() => {
        const filtros: any = {};
        if (abaAtiva !== 'todas') {
            filtros.tipo = abaAtiva;
        }
        if (termoBusca.trim()) {
            filtros.termo = termoBusca.trim();
        }
        return filtros;
    }, [abaAtiva, termoBusca]);

    const movimentosQuery = useMovimentos(inventario?.id, filtrosMovimentos);
    const estatisticasQuery = useEstatisticasMovimentos(inventario?.id);

    // Verificar se há múltiplas empresas selecionadas
    const hasMultipleEmpresas = selectedIds.length > 1;
    const empresaSelecionada = empresas.find(e => e.id === selectedIds[0]);

    // Verifica se há erros de permissão
    const hasPermissionError = inventarioQuery.isError;

    // Filtrar movimentos baseado no termo de busca local
    const movimentosFiltrados = useMemo(() => {
        if (!movimentosQuery.data?.data || !termoBusca.trim()) {
            return movimentosQuery.data?.data || [];
        }

        const termo = termoBusca.toLowerCase();
        return movimentosQuery.data.data.filter(movimento =>
            movimento.produtoDescricao?.toLowerCase().includes(termo) ||
            movimento.produtoCodigo?.toString().includes(termo) ||
            movimento.numero?.toLowerCase().includes(termo) ||
            movimento.rms?.toLowerCase().includes(termo) ||
            movimento.numeroNota?.toLowerCase().includes(termo) ||
            movimento.fornecedorNome?.toLowerCase().includes(termo) ||
            movimento.medicoNome?.toLowerCase().includes(termo)
        );
    }, [movimentosQuery.data?.data, termoBusca]);

    // Contadores por tipo para as abas
    const contadoresPorTipo = useMemo(() => {
        const movimentos = movimentosQuery.data?.data || [];
        return {
            todas: movimentos.length,
            ENTRADA: movimentos.filter(m => m.tipo === 'ENTRADA').length,
            VENDA_CONSUMIDOR: movimentos.filter(m => m.tipo === 'VENDA_CONSUMIDOR').length,
            PERDA: movimentos.filter(m => m.tipo === 'PERDA').length,
        };
    }, [movimentosQuery.data?.data]);

    // Handlers para modais
    const handleCriarMovimento = (tipo: 'ENTRADA' | 'VENDA_CONSUMIDOR' | 'PERDA') => {
        setTipoMovimentoCriar(tipo);
        setShowModalCriarMovimento(true);
    };

    const handleEditarMovimento = (movimento: any) => {
        setMovimentoParaEditar(movimento);
        setShowModalEditarMovimento(true);
    };

    const handleExcluirMovimento = (movimento: any) => {
        setMovimentoParaExcluir(movimento);
        setShowModalExcluirMovimento(true);
    };

    const handleImportarLotes = (tipo: 'vendas' | 'compras') => {
        setTipoImportacao(tipo);
        setShowModalImportarLotes(true);
    };

    if (inventarioQuery.isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    if (hasPermissionError) {
        return (
            <div className="text-center py-5 text-danger">
                {(inventarioQuery.error as Error).message}
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={movimentacaoBreadcrumbItems} />

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-2">
                        Movimentação
                    </h2>
                    <p className="text-body-tertiary mb-0">
                        Gerencie os movimentos de produtos controlados (entradas, vendas e perdas)
                    </p>
                </div>

                {/* Mostrar empresa selecionada */}
                {empresaSelecionada && (
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center text-muted">
                            <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                            <small>
                                <strong>{empresaSelecionada.nomeFantasia}</strong>
                            </small>
                        </div>
                        {inventario && (
                            <div className="d-flex align-items-center gap-1 ms-2">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-primary me-1" size="sm" />
                                    <small className="text-muted">
                                        Inventário: {format(new Date(inventario.data), 'dd/MM/yyyy')}
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Alerta para múltiplas empresas */}
            {hasMultipleEmpresas && (
                <Alert variant="warning" className="mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Múltiplas empresas selecionadas:</strong> Para trabalhar com movimentos SNGPC,
                    selecione apenas uma empresa no seletor de empresas. Atualmente está sendo usada: <strong>{empresaSelecionada?.nomeFantasia}</strong>
                </Alert>
            )}

            {/* Alerta quando nenhuma empresa está selecionada */}
            {selectedIds.length === 0 && (
                <Alert variant="warning" className="mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Nenhuma empresa selecionada:</strong> Selecione uma empresa no seletor de empresas para trabalhar com movimentos SNGPC.
                </Alert>
            )}

            {!inventario && selectedIds.length > 0 ? (
                // Estado: Nenhum inventário ativo
                <Card className="text-center py-5 mb-4">
                    <Card.Body>
                        <FontAwesomeIcon icon={faChartBar} size="4x" className="text-muted mb-4" />
                        <h3 className="mb-3">Nenhum Inventário SNGPC Ativo</h3>
                        <p className="text-body-tertiary mb-4 lead">
                            Para gerenciar movimentos, é necessário ter um inventário SNGPC ativo e validado.
                        </p>
                        <Button
                            variant="primary"
                            href="/sngpc/inventario"
                        >
                            <FontAwesomeIcon icon={faPlay} className="me-2" />
                            Ir para Inventário
                        </Button>
                    </Card.Body>
                </Card>
            ) : inventario && !inventario.validado ? (
                // Estado: Inventário não validado
                <Card className="text-center py-5 mb-4">
                    <Card.Body>
                        <FontAwesomeIcon icon={faClock} size="4x" className="text-warning mb-4" />
                        <h3 className="mb-3">Inventário Não Validado</h3>
                        <p className="text-body-tertiary mb-4 lead">
                            O inventário precisa ser validado antes de poder registrar movimentos.
                        </p>
                        <Button
                            variant="warning"
                            href="/sngpc/inventario"
                        >
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                            Validar Inventário
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {/* Cards de Estatísticas */}
                    {estatisticasQuery.data?.data && (
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="border-success">
                                    <Card.Body className="text-center">
                                        <FontAwesomeIcon icon={faShoppingCart} size="2x" className="text-success mb-2" />
                                        <h4 className="mb-1">{estatisticasQuery.data.data.totalEntradas}</h4>
                                        <small className="text-muted">Entradas</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-info">
                                    <Card.Body className="text-center">
                                        <FontAwesomeIcon icon={faReceipt} size="2x" className="text-info mb-2" />
                                        <h4 className="mb-1">{estatisticasQuery.data.data.totalVendas}</h4>
                                        <small className="text-muted">Vendas</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-danger">
                                    <Card.Body className="text-center">
                                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-danger mb-2" />
                                        <h4 className="mb-1">{estatisticasQuery.data.data.totalPerdas}</h4>
                                        <small className="text-muted">Perdas</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-primary">
                                    <Card.Body className="text-center">
                                        <FontAwesomeIcon icon={faChartBar} size="2x" className="text-primary mb-2" />
                                        <h4 className="mb-1">
                                            {estatisticasQuery.data.data.totalEntradas +
                                                estatisticasQuery.data.data.totalVendas +
                                                estatisticasQuery.data.data.totalPerdas}
                                        </h4>
                                        <small className="text-muted">Total</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Interface de Movimentos */}
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Movimentos</h5>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => handleImportarLotes('vendas')}
                                    disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                >
                                    <FontAwesomeIcon icon={faFileImport} className="me-2" />
                                    Importar Vendas
                                </Button>
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleImportarLotes('compras')}
                                    disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                >
                                    <FontAwesomeIcon icon={faFileImport} className="me-2" />
                                    Importar Compras
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {/* Navegação por abas */}
                            <Tab.Container
                                id="movimentos-tabs"
                                activeKey={abaAtiva}
                                onSelect={(k) => setAbaAtiva(k as any)}
                            >
                                <Nav variant="tabs" className="mb-3">
                                    <Nav.Item>
                                        <Nav.Link eventKey="todas">
                                            Todas
                                            {contadoresPorTipo.todas > 0 && (
                                                <Badge bg="secondary" className="ms-2">
                                                    {contadoresPorTipo.todas}
                                                </Badge>
                                            )}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="ENTRADA">
                                            <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                                            Entradas
                                            {contadoresPorTipo.ENTRADA > 0 && (
                                                <Badge bg="success" className="ms-2">
                                                    {contadoresPorTipo.ENTRADA}
                                                </Badge>
                                            )}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="VENDA_CONSUMIDOR">
                                            <FontAwesomeIcon icon={faReceipt} className="me-2" />
                                            Vendas
                                            {contadoresPorTipo.VENDA_CONSUMIDOR > 0 && (
                                                <Badge bg="info" className="ms-2">
                                                    {contadoresPorTipo.VENDA_CONSUMIDOR}
                                                </Badge>
                                            )}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="PERDA">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                            Perdas
                                            {contadoresPorTipo.PERDA > 0 && (
                                                <Badge bg="danger" className="ms-2">
                                                    {contadoresPorTipo.PERDA}
                                                </Badge>
                                            )}
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>

                                <Tab.Content>
                                    <Tab.Pane eventKey={abaAtiva}>
                                        {/* Filtros e Busca */}
                                        <Row className="mb-3 align-items-center">
                                            <Col md={6}>
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon={faSearch} />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Buscar por produto, código, lote, RMS, nota ou fornecedor..."
                                                        value={termoBusca}
                                                        onChange={(e) => setTermoBusca(e.target.value)}
                                                    />
                                                    {termoBusca && (
                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={() => setTermoBusca('')}
                                                            title="Limpar busca"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </Button>
                                                    )}
                                                </InputGroup>
                                            </Col>
                                            <Col md={6} className="text-end">
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => handleCriarMovimento('ENTRADA')}
                                                        disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                        Entrada
                                                    </Button>
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        onClick={() => handleCriarMovimento('VENDA_CONSUMIDOR')}
                                                        disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                        Venda
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleCriarMovimento('PERDA')}
                                                        disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                        Perda
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Lista de Movimentos */}
                                        {movimentosQuery.isLoading ? (
                                            <div className="text-center py-4">
                                                <Spinner animation="border" />
                                                <p className="mt-2 text-muted">Carregando movimentos...</p>
                                            </div>
                                        ) : movimentosFiltrados.length === 0 ? (
                                            <div className="text-center py-4">
                                                <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                                                <h5 className="text-muted mb-3">
                                                    {termoBusca ? 'Nenhum movimento encontrado' : 'Nenhum movimento registrado'}
                                                </h5>
                                                <p className="text-muted mb-4">
                                                    {termoBusca
                                                        ? `Não foram encontrados movimentos que correspondam à busca "${termoBusca}".`
                                                        : 'Registre movimentos de entrada, venda ou perda para produtos controlados.'
                                                    }
                                                </p>
                                                {termoBusca ? (
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={() => setTermoBusca('')}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                                                        Limpar Busca
                                                    </Button>
                                                ) : (
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <Button
                                                            variant="success"
                                                            onClick={() => handleCriarMovimento('ENTRADA')}
                                                            disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                            Nova Entrada
                                                        </Button>
                                                        <Button
                                                            variant="info"
                                                            onClick={() => handleCriarMovimento('VENDA_CONSUMIDOR')}
                                                            disabled={hasMultipleEmpresas || selectedIds.length === 0 || inventario?.finalizado}
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                            Nova Venda
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Tipo</th>
                                                            <th>Produto</th>
                                                            <th>Lote</th>
                                                            <th>Quantidade</th>
                                                            <th>Data</th>
                                                            <th>Status</th>
                                                            <th>Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {movimentosFiltrados.map((movimento) => {
                                                            const tipoBadge = getMovimentoTipoBadge(movimento.tipo);
                                                            const transmitido = !!movimento.transmissaoId;

                                                            return (
                                                                <tr key={movimento.id}>
                                                                    <td>
                                                                        <Badge bg={tipoBadge.variant}>
                                                                            {tipoBadge.text}
                                                                        </Badge>
                                                                    </td>
                                                                    <td>
                                                                        <div>
                                                                            <LinkProduto produtoId={movimento.produtoId}>
                                                                                <strong>{movimento.produtoDescricao}</strong>
                                                                            </LinkProduto>
                                                                            <div className="small text-muted">
                                                                                Código: {movimento.produtoCodigo}
                                                                                {movimento.rms && ` | RMS: ${movimento.rms}`}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-secondary">
                                                                            {movimento.numero}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span className="fw-bold">{movimento.quantidade}</span>
                                                                    </td>
                                                                    <td>
                                                                        <small>
                                                                            {format(new Date(movimento.data), 'dd/MM/yyyy')}
                                                                        </small>
                                                                    </td>
                                                                    <td>
                                                                        {transmitido ? (
                                                                            <Badge bg="success">
                                                                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                                                Transmitido
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge bg="warning">
                                                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                                Pendente
                                                                            </Badge>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <Button
                                                                                variant="outline-primary"
                                                                                size="sm"
                                                                                onClick={() => handleEditarMovimento(movimento)}
                                                                                disabled={transmitido || inventario?.finalizado || hasMultipleEmpresas || selectedIds.length === 0}
                                                                                title="Editar movimento"
                                                                            >
                                                                                <FontAwesomeIcon icon={faEdit} />
                                                                            </Button>
                                                                            <Button
                                                                                variant="outline-danger"
                                                                                size="sm"
                                                                                onClick={() => handleExcluirMovimento(movimento)}
                                                                                disabled={transmitido || inventario?.finalizado || hasMultipleEmpresas || selectedIds.length === 0}
                                                                                title="Excluir movimento"
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrash} />
                                                                            </Button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </Card.Body>
                    </Card>
                </>
            )}

            {/* Placeholder para modais que serão implementados */}
            <ModalCriarMovimento
                show={showModalCriarMovimento}
                onHide={() => setShowModalCriarMovimento(false)}
                tipo={tipoMovimentoCriar}
                inventarioId={inventario?.id}
            />
        </div>
    );
};

export default MovimentacaoSngpc;
